import React, { FC } from 'react';
import styled from 'styled-components';
import { Button, Icon, ModalContent, ModalHeader, ModalTitle } from 'components';
import { theme, toRem } from 'design';
import { IEmployee, IEmployerSite, IPOSProvider, ISiteIntegration } from 'types';
import { formatIntegrationName } from 'utils';
import { ContentFooter, Layout, SplitScreen, StyledSubtitle, Wrapper } from '../styles';
import EmployeeInfo from './EmployeeInfo';

interface IConfirmProps {
  dataCy: string;
  integration: ISiteIntegration;
  provider: IPOSProvider;
  employee: IEmployee;
  site: IEmployerSite;
  isProcessing: boolean;
  onConfirm(employee: IEmployee, site: IEmployerSite): void;
  onClose(): void;
}

const IconWrapper = styled.div`
  position: relative;
  padding: ${toRem(theme.space.sm)};
  border-radius: 50%;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  background-color: ${theme.color.white};
  height: ${toRem(36)};
  width: ${toRem(36)};
  top: ${toRem(-18)};
  margin: auto;

  svg {
    height: ${toRem(20)};
    width: ${toRem(20)};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    top: auto;
    left: ${toRem(-18)};
  }
`;

const Split = styled.div`
  position: relative;
  height: ${toRem(1)};
  background-color: ${theme.color.neutral[200]};
  width: 100%;

  @media (min-width: ${theme.breakpoint.small}px) {
    width: ${toRem(1)};
    height: 100%;
  }
`;

const Confirm: FC<IConfirmProps> = ({ provider, integration, employee, site, isProcessing, onConfirm, onClose, dataCy }: IConfirmProps) => (
  <Layout isActive onClose={onClose} data-cy={`${dataCy}-confirm`} maxWidth={540}>
    <ModalHeader>
      <ModalTitle data-cy="modal-title">Connect {formatIntegrationName(integration)} Employee To Kickfin</ModalTitle>
      <StyledSubtitle data-cy="modal-subtitle">Create a connection between employees on your POS to Kickfin users.</StyledSubtitle>
    </ModalHeader>
    <ModalContent>
      <Wrapper>
        <SplitScreen data-cy="split">
          <EmployeeInfo name={provider.name} type={provider.type} employeeId={provider.customerEmployeeId} dataCy={dataCy} showBorder={false} width={240} />
          <Split>
            <IconWrapper>
              <Icon icon="Link" fill={theme.color.secondary[400]} />
            </IconWrapper>
          </Split>
          <EmployeeInfo name={employee.name} employeeId={employee.sites.find((s) => s.id === site.id).employeeId} type="kickfin" dataCy={dataCy} showBorder={false} width={240} />
        </SplitScreen>
        <Wrapper>
          <ContentFooter>
            <Button appearance="primary" label="Connect" isProcessing={isProcessing} onClick={() => onConfirm(employee, site)} />
          </ContentFooter>
        </Wrapper>
      </Wrapper>
    </ModalContent>
  </Layout>
);

export default Confirm;
